import React, { useRef, useCallback, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import head from 'lodash/head';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';
import partial from 'lodash/partial';
import { selectTicket, selectInboxTab, selectInboxTabWithLoad, setShowTools } from 'actions';
import { getCustomerAvatar } from 'util/avatarUtil';
import * as colors from 'components/common/colorConstants';
import ExpiryTimer from 'components/inboxPanel/ExpiryTimer/ExpiryTimer';
import { getExpiryTimeForTicket, showTicketExpiryTimer } from 'util/timerUtil';
import ContextualPopover from 'components/popups/ContextualPopover/ContextualPopover';
import { removeNotification } from 'actions/notificationActions';
import {
  extractSelectedTicketIdFromState,
  extractParseConfigValueFromState,
  extractTicketFromStateById,
} from 'extractFromState';
import { TICKET_STATUS_ASSIGNED } from 'constants/ticketStatuses';
import AlertCircleIcon from 'components/Widgets/AlertCircleIcon/AlertCircleIcon';
import { useBoundingClientRect } from 'customHooks';
import {
  useChatQuickReplies,
  useAssignedChatPopover,
  useCustomerReplyNotification,
  useExpertTimeoutNotification,
} from './hooks';
import { Section, Tab, UnreadCountWrapper, PopoverWrapper } from './ChatTab.styles';
import { isLiveChat } from 'util/chat';
import {
  isValidPartnerReachOutTicket,
  hasPartnerRespondedToReachOut,
  partnerReachOutRetryNeeded,
} from 'util/ticketSelector';

const propTypes = {
  ticket: PropTypes.shape({
    ticketId: PropTypes.string.isRequired,
    ticketStatus: PropTypes.string,
    lastMessageId: PropTypes.string,
    lastMessageDirection: PropTypes.string,
    customerFirstName: PropTypes.string,
    unreadCount: PropTypes.number,
  }).isRequired,
};

const ChatTab = ({ containerRef, ticket, timeoutPeriod = 0, history }) => {
  const dispatch = useDispatch();

  const firstNotification = useSelector((state) => head(state.notifications.tabNotificationsQueue));
  const firstNotificationTicket = useSelector((state) =>
    extractTicketFromStateById(state, get(firstNotification, 'ticketId'))
  );
  const selectedTicketId = useSelector(extractSelectedTicketIdFromState);
  const isChatQuickRepliesEnabledGlobalConfig = useSelector((state) =>
    extractParseConfigValueFromState(state, 'enableChatQuickReplies', false)
  );

  const { error, isLoading, quickReplies, recentMessageBodies, trackQuickRepliesDisplayed } = useChatQuickReplies(
    ticket,
    isChatQuickRepliesEnabledGlobalConfig
  );

  const tabName = isLiveChat(ticket.communicationType) ? 'live_chat' : 'assigned';
  const isUpsellTicket = !!ticket.moreTicketInfo?.humanAndBotBothActiveInChat;
  const tabRef = useRef(null);
  const tabClientRect = useBoundingClientRect(tabRef);
  const tabContainerClientRect = useBoundingClientRect(containerRef);
  const [tabContainerScrollPosition, setTabContainerScrollPosition] = useState(0);

  // Keep track of the chat tab scroll position to place the popover at the correct height.
  useLayoutEffect(() => {
    const handleScroll = () => {
      const position = containerRef.current?.scrollTop;
      setTabContainerScrollPosition(position);
    };

    const containerEl = containerRef.current;
    containerEl?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      containerEl?.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef]);

  const { isPopoverOpen, openPopover, closePopover } = useAssignedChatPopover(ticket);

  // onNotificationClick is invoked when the expert is on a different tab or has the platform minimized.
  // This function brings the user back to the platform and automatically clicks <ChatTab>.
  const onNotificationClick = useCallback(() => {
    window.focus();

    if (tabRef.current) {
      tabRef.current.click();
    }
  }, []);

  const { isIndicatorVisible: isReplyIndicatorVisible } = useCustomerReplyNotification(ticket, {
    onNotificationClick,
  });

  const { isIndicatorVisible: isExpertTimeoutIndicatorVisible } = useExpertTimeoutNotification(ticket, {
    timeoutPeriod,
    onNotificationClick,
  });

  const navigateHome = () => {
    if (history && isFunction(history.push)) {
      history.push('/home');
    }
  };

  const openToolsForPartnerReachOut = () => {
    if (isValidPartnerReachOutTicket(ticket)) {
      dispatch(setShowTools(true));
    }
  };

  const handleTabClick = async () => {
    navigateHome();
    dispatch(selectInboxTab(tabName));
    await dispatch(selectTicket(ticket.ticketId, tabName));
    openToolsForPartnerReachOut();
    if (!isPopoverOpen) {
      openPopover();
    } else {
      closePopover();
    }
  };

  const canOpenPopover = isReplyIndicatorVisible || isExpertTimeoutIndicatorVisible;

  /**
   * Animate the chat tab in the following cases:
   * 1. If partner reach out ticket, only if partner has responded
   * 2. The chat tab corresponds with the most recently nominated chat.
   * 3. The expert timeout period has elapsed.
   */
  const shouldChatAnimate =
    isExpertTimeoutIndicatorVisible ||
    (firstNotificationTicket?.ticketStatus === TICKET_STATUS_ASSIGNED &&
      firstNotificationTicket.ticketId === ticket.ticketId);

  const isAnimating = isValidPartnerReachOutTicket(ticket)
    ? hasPartnerRespondedToReachOut(ticket) || partnerReachOutRetryNeeded(ticket)
    : shouldChatAnimate;

  const displayName = ticket.customerFirstName || 'Chat';
  const selected = selectedTicketId === ticket.ticketId;
  const showUnreadInfo = showTicketExpiryTimer(ticket, false);

  if (error) {
    return null;
  }

  return (
    <>
      <Section>
        <Tab
          role="button"
          onClick={handleTabClick}
          onMouseEnter={canOpenPopover ? openPopover : noop}
          selected={selected}
          showUnreadInfo={showUnreadInfo}
          isAlertDotVisible={isReplyIndicatorVisible && !isValidPartnerReachOutTicket(ticket)}
          animateBounce={isAnimating}
          ref={tabRef}
        >
          <img src={getCustomerAvatar(ticket)} alt={`avatar_${ticket.ticketId}`} />
          <p>{displayName}</p>
          {isReplyIndicatorVisible && !isValidPartnerReachOutTicket(ticket) && (
            <UnreadCountWrapper>
              <AlertCircleIcon count={ticket.unreadCount || 1} color={colors.green1} isUpsellTicket={isUpsellTicket} />
            </UnreadCountWrapper>
          )}
          {showUnreadInfo && (
            <ExpiryTimer loadingBar chatTab {...ticket} ticketExpiresIn={getExpiryTimeForTicket(ticket)} />
          )}
        </Tab>
      </Section>

      {isPopoverOpen && renderPopover()}
    </>
  );

  function renderPopover() {
    if (!(tabContainerClientRect && tabClientRect)) {
      return null;
    }

    const chatTabTop = tabClientRect.absoluteTop - tabContainerScrollPosition;
    const chatTabBottom = tabClientRect.absoluteBottom - tabContainerScrollPosition;

    // Check whether the chat tab is placed outside of the container.
    if (chatTabTop <= tabContainerClientRect.absoluteTop || chatTabBottom >= tabContainerClientRect.absoluteBottom) {
      return null;
    }

    const handleViewClick = async () => {
      navigateHome();
      dispatch(selectInboxTabWithLoad(tabName));
      await dispatch(selectTicket(ticket.ticketId, tabName));
      openToolsForPartnerReachOut();
      dispatch(removeNotification(ticket.ticketId));
      closePopover();
    };

    return (
      <PopoverWrapper top={chatTabTop} left={tabClientRect.right}>
        <ContextualPopover
          isLoading={isLoading}
          labelButtonsWithActions={[
            ['View', handleViewClick],
            ['Dismiss', closePopover],
          ]}
          ticket={ticket}
          ticketAssigned={true}
          isQuickRepliesEnabled={isChatQuickRepliesEnabledGlobalConfig && !isValidPartnerReachOutTicket(ticket)}
          quickReplies={quickReplies}
          messages={recentMessageBodies}
          trackQuickRepliesDisplayed={trackQuickRepliesDisplayed}
          closePopover={closePopover}
          onClose={partial(removeNotification, ticket.ticketId)}
        />
      </PopoverWrapper>
    );
  }
};

ChatTab.propTypes = propTypes;

export default withRouter(ChatTab);
